/** @jsxImportSource @emotion/react */
import { StyledRow } from 'components/atoms/DataTable'
import { ApplyPaymentModal } from 'components/lab/billingEraReports/ApplyPaymentModal'
import { ERA_CLAIM_ARCHIVE_FAILURE_LABELS } from 'components/lab/billingEraReports/constants'
import { NeedsWorkFlagModal } from 'components/lab/billingNeedsWork/NeedsWorkFlagModal'
import { EraClaimSelection } from 'components/lab/billingEraReports/types'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import { StatePill } from 'components/lab/atoms/StatePill'
import { FORWARDED_COLOR } from 'components/lab/billingCharge/utils'
import { BillingLoading } from 'components/lab/billingEraReports/BillingLoading'
import { ClaimIdentifierLink, ClaimPatientName } from 'components/lab/billingEraReports/EraClaimChargeLinks'
import {
    explainAutoPost,
    getClaimReviewState,
    getClaimStateColor,
    getClaimStateLabel,
    getPostingActionLabel,
  isClaimArchivable,
} from 'components/lab/billingEraReports/claimState'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import { normalizeCas } from 'components/lab/billingEraReports/utils'
import { toNum } from 'components/lab/orderEdit/helpers'
import { confirm } from 'components/molecules/Confirmation'
import { downloadEobPdf } from 'helpers/eob'
import { BillingEraReportsListQuery, BillingTransactionClaim, PaymentServiceLine, ServiceAdjustment } from 'store/types'
import { isForwardedClaim } from 'ts-common/utils/eraCrossover.utils'
import React from 'react'
import { Button } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useArchiveBillingEraClaimMutation, useGetTransactionClaimsQuery, } from 'store/services/billingCharges'
import { useGetEraPostingPlanQuery } from 'store/services/billingPayments'
import { AdjustmentCode, RemarkCodeList } from 'components/lab/billingEraReports/AdjustmentCodes'
import { formatCalendarDate } from 'helpers/dates'

const Money = ({ v }: { v?: string | number }) => (
  <span>{v ? `$${Number(v).toFixed(2)}` : '$0.00'}</span>
)

// Why the remittance did not post itself, on hover: a clock for a hold that
// posts on its own, a warning for an exception someone has to settle.
export const AutoPostIndicator = ({ claim }: { claim: BillingTransactionClaim }) => {
  const explanation = explainAutoPost(claim)

  if (!explanation) {
    return null
  }

  return (
    <TooltipWrapper
      show
      maxWidth={360}
      text={() => (
        <div css={{ textAlign: 'left' }}>
          <div css={{ fontWeight: 600 }}>{explanation.title}</div>
          {explanation.reasons.map((reason) => (
            <div key={reason}>• {reason}</div>
          ))}
        </div>
      )}>
      <i
        className={`fa ${explanation.state === 'EXCEPTION' ? 'fa-exclamation-circle' : 'fa-clock'}`}
        aria-label={[explanation.title, ...explanation.reasons].join('. ')}
        css={{ color: explanation.color, cursor: 'help', fontSize: 15 }}
      />
    </TooltipWrapper>
  )
}

const Dot = ({ color = '#29C788', title }: { color?: string; title?: string }) => (
  <span
    title={title}
    css={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 8,
      background: color,
      marginRight: 6,
    }}
  />
)

// CLP02 — authoritative claim status. Legacy claims imported before the CLP
// fields were persisted have no statusCode and fall through to line analysis.
const getStatusCode = (claim: BillingTransactionClaim): string | undefined =>
  claim.claimStatusCode || undefined

type PayerSequence = 'PRIMARY' | 'SECONDARY' | 'TERTIARY'

const hasOa23 = (adjustments?: ServiceAdjustment[] | null): boolean =>
  (adjustments || []).some((adj) => {
    const reasons = [
      adj.adjustmentReasonCode1,
      adj.adjustmentReasonCode2,
      adj.adjustmentReasonCode3,
      adj.adjustmentReasonCode4,
      adj.adjustmentReasonCode5,
      adj.adjustmentReasonCode6,
    ]

    return adj.claimAdjustmentGroupCode === 'OA' && reasons.includes('23')
  })

const getPayerSequence = (claim: BillingTransactionClaim): PayerSequence => {
  const statusCode = getStatusCode(claim)

  if (statusCode === '2' || statusCode === '20') return 'SECONDARY'
  if (statusCode === '3' || statusCode === '21') return 'TERTIARY'
  if (statusCode === '1' || statusCode === '19') return 'PRIMARY'

  // Fallback when CLP02 carries no sequence (4/22/23/25, legacy rows):
  // OA-23 (impact of prior payer's adjudication) means an earlier payer
  // already processed this claim — reported at claim level or line level.
  const hasPriorPayerAdjustment =
    hasOa23(claim.claimAdjustments) ||
    (claim.serviceLines ?? []).some((line) => hasOa23(line?.serviceAdjustments))

  if (hasPriorPayerAdjustment) {
    return 'SECONDARY'
  }

  return 'PRIMARY'
}

const getPayerSequenceIndicator = (claim: BillingTransactionClaim): 'P' | 'S' | 'T' => {
  const sequence = getPayerSequence(claim)

  if (sequence === 'SECONDARY') return 'S'
  if (sequence === 'TERTIARY') return 'T'
  return 'P'
}

const getPayerSequenceBadgeStyle = (indicator: 'P' | 'S' | 'T') => {
  if (indicator === 'P') {
    return { backgroundColor: '#0d6efd', color: '#ffffff' }
  }

  if (indicator === 'T') {
    return { backgroundColor: '#6f42c1', color: '#ffffff' }
  }

  return { backgroundColor: '#f0ad4e', color: '#212529' }
}

const SequenceBadge = ({ indicator }: { indicator: 'P' | 'S' | 'T' }) => (
  <span
    css={{
      ...getPayerSequenceBadgeStyle(indicator),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 20,
      height: 20,
      borderRadius: 5,
      fontSize: 12,
      fontWeight: 700,
    }}>
    {indicator}
  </span>
)

// Holds a cell's width while the posting plan is in flight.
const Pending = () => (
  <span
    aria-hidden="true"
    css={{
      display: 'inline-block',
      width: '3.2em',
      height: '0.75em',
      borderRadius: 3,
      background: '#e4e7ea',
    }}
  />
)

const EraServiceLinesSummary: React.FC<{
  lines?: PaymentServiceLine[]
  pendingRows?: number
}> = ({ lines, pendingRows = 1 }) => {
  if (!lines) {
    // The claim already tells us how many rows there will be, so the skeleton
    // takes the same shape as the loaded table — one row per service line plus
    // the total — holding the panel's height steady until the amounts land.
    return (
      <>
        {Array.from({ length: pendingRows }).map((_, idx) => (
          <tr key={idx}>
            <td>{idx + 1}</td>
            {Array.from({ length: 6 }).map((__, cell) => (
              <td key={cell}>
                <Pending />
              </td>
            ))}
          </tr>
        ))}
        <tr style={{ fontWeight: 600 }}>
          <td colSpan={2}>Total</td>
          {Array.from({ length: 5 }).map((_, cell) => (
            <td key={cell}>
              <Pending />
            </td>
          ))}
        </tr>
      </>
    )
  }

  let tBalance = 0
  let tAllowed = 0
  let tPaid = 0
  let tCopay = 0
  let tWriteOff = 0

  const rows = lines.map((sl, idx) => {
    const billed = toNum(sl.balance)
    const allowed = toNum(sl.allowed)
    const paid = toNum(sl.paidRefund)
    const prSum = toNum(sl.copay)
    const coSum = toNum(sl.writeOff)

    tBalance += billed
    tAllowed += allowed
    tPaid += paid
    tCopay += prSum
    tWriteOff += coSum

    return (
      <tr key={idx}>
        <td>{idx + 1}</td>
        <td>{sl.cptCode}</td>
        <td>
          <Money v={billed} />
        </td>
        <td>
          <Money v={allowed} />
        </td>
        <td>
          <Money v={paid} />
        </td>
        <td>
          <Money v={prSum} />
        </td>
        <td>
          <Money v={coSum} />
        </td>
      </tr>
    )
  })

  return (
    <>
      {rows}
      <tr style={{ fontWeight: 600 }}>
        <td colSpan={2}>Total</td>
        <td>
          <Money v={tBalance} />
        </td>
        <td>
          <Money v={tAllowed} />
        </td>
        <td>
          <Money v={tPaid} />
        </td>
        <td>
          <Money v={tCopay} />
        </td>
        <td>
          <Money v={tWriteOff} />
        </td>
      </tr>
    </>
  )
}

interface ClaimProps {
  claim: BillingTransactionClaim
  eraId: string
  selection?: EraClaimSelection
}

const ClaimRow: React.FC<ClaimProps> = ({ claim, eraId, selection }) => {
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [archiveEraClaim] = useArchiveBillingEraClaimMutation()
  const [rowOpen, setRowOpen] = React.useState(false)
  const [showModal, setShowModal] = React.useState(false)
  const [parking, setParking] = React.useState(false)
  const [planWanted, setPlanWanted] = React.useState(false)
  const [eobLoading, setEobLoading] = React.useState(false)
  const claimId = claim?.claimNumber
  const payerClaimNumber = claim?.payerClaimControlNumber || '-'
  const paid = claim?.paidAmount
  const responsible = claim?.responsible
  const nextResponsible = claim?.nextResponsible || '-'
  const firstServiceDate = claim?.dateOfService || undefined
  const payerSequenceIndicator = getPayerSequenceIndicator(claim)
  const claimStatus = getClaimReviewState(claim)
  const claimStatusColor = getClaimStateColor(claimStatus)
  const isForwarded = isForwardedClaim(claim)
  const columnCount = selection ? 12 : 11
  const archiveFailure = selection?.failures.get(claim.id)

  // Fetched when the row opens (the expanded tables show the derived amounts)
  // or when the biller reaches for Apply Payment — the modal reads this same
  // cache entry, so warming it here is what keeps the form from opening on an
  // empty table.
  const { data: postingPlan } = useGetEraPostingPlanQuery(
    { eraId, claimId: claim?.id as string },
    { skip: (!rowOpen && !planWanted) || !claim?.id },
  )
  const derivedLines = postingPlan?.serviceLines

  return (
    <>
      <StyledRow
        onClick={() => setRowOpen((p) => !p)}
        css={{
          cursor: 'pointer',
          '& > td': { border: 'none' },
          borderBottom: '1px solid #ededed',
          background: archiveFailure ? '#fdecea' : undefined,
        }}>
        {selection && (
          <td onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              disabled={!isClaimArchivable(claim)}
              title={isClaimArchivable(claim) ? undefined : 'Posted or archived claims cannot be archived'}
              checked={selection.selectedIds.has(claim.id)}
              onChange={() => selection.toggleRow(claim.id)}
            />
          </td>
        )}
        <td css={{ whiteSpace: 'nowrap' }}>
          <Dot color={claimStatusColor} title={getClaimStateLabel(claimStatus)} />
          <span css={{ color: claimStatusColor, fontWeight: 600, fontSize: 12 }}>
            {getClaimStateLabel(claimStatus)}
          </span>
          {isForwarded && (
            <span css={{ marginLeft: 6 }}>
              <StatePill
                color={FORWARDED_COLOR}
                label="Forwarded"
                tooltip={
                  nextResponsible && nextResponsible !== '-'
                    ? `Forwarded to ${nextResponsible}`
                    : 'Forwarded to additional payer'
                }
              />
            </span>
          )}
        </td>
        <td css={{ textAlign: 'center' }}>
          <AutoPostIndicator claim={claim} />
        </td>
        <td>
          <SequenceBadge indicator={payerSequenceIndicator} />
        </td>
        <td
          className="d-flex align-items-center"
          css={{
            fontWeight: 600,
          }}>
          <ClaimIdentifierLink claim={claim} />
        </td>
        <td css={{ fontWeight: 600 }}>{payerClaimNumber}</td>
        <td>
          <ClaimPatientName claim={claim} />
        </td>
        <td>{formatCalendarDate(firstServiceDate)}</td>
        <td>
          <Money v={paid} />
        </td>
        <td>{responsible}</td>
        <td>{nextResponsible || '—'}</td>
        <td>
          <Button
            size="sm"
            className="mr-2"
            variant="outline-dark"
            disabled={eobLoading}
            onClick={async (e) => {
              e.stopPropagation()
              setEobLoading(true)
              await downloadEobPdf(
                `/lab/billing/era-reports/${eraId}/claims/${claim.id}/eob`,
                `EOB_Claim_${claim.claimNumber || claim.id}.pdf`,
              )
              setEobLoading(false)
            }}>
            {eobLoading ? 'Print EOB…' : 'Print EOB'}
          </Button>
          {archiveFailure && (
            <span className="text-danger mr-2" css={{ fontWeight: 600 }}>
              Not archived: {ERA_CLAIM_ARCHIVE_FAILURE_LABELS[archiveFailure]}
            </span>
          )}
          {claim?.paymentId && (
            <span className={'text-success'} css={{ fontWeight: 600 }}>
              Payment applied
            </span>
          )}
          {claim?.denialId && !claim?.paymentId && (
            <span className={'text-danger'} css={{ fontWeight: 600 }}>
              Denial recorded
            </span>
          )}
          {claim?.archived && (
            <>
              <span css={{ fontWeight: 600 }}>Claim archived</span>
              {canPostInsurancePayments && (
                <Button
                  size="sm"
                  className="ml-2"
                  variant="outline-secondary"
                  onClick={async (e) => {
                    e.stopPropagation()

                    confirm('', {
                      title: 'Are you sure?',
                      description: 'Are you sure you want to un-archive this claim?',
                      yesAction: 'Yes',
                    })
                      .then(async () => {
                        try {
                          await archiveEraClaim({
                            id: eraId,
                            eraId,
                            claimId: claim.id,
                            archived: false,
                          }).unwrap()
                          toast.success('Claim un-archived successfully')
                        } catch (e) {
                          toast.error(`There was an error un-archiving this claim`)
                        }
                      })
                      .catch(() => {})
                  }}>
                  Un-archive claim
                </Button>
              )}
            </>
          )}
          {claim?.parkedAt && (
            <span
              css={{ fontWeight: 600, color: '#E8590C' }}
              title="Parked under Needs Work on its charge — nothing posted until the item is resolved">
              Parked — Needs Work
            </span>
          )}
          {canPostInsurancePayments && !claim?.paymentId && !claim?.archived && !claim?.parkedAt && !claim?.denialId && (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                onMouseEnter={() => setPlanWanted(true)}
                onFocus={() => setPlanWanted(true)}
                onClick={(e) => {
                  e.stopPropagation()
                  setPlanWanted(true)
                  setShowModal(true)
                }}>
                {getPostingActionLabel(claimStatus)}
              </Button>
              <Button
                size="sm"
                className="ml-2"
                variant="outline-secondary"
                onClick={async (e) => {
                  e.stopPropagation()

                  confirm('', {
                    title: 'Are you sure?',
                    description: 'Are you sure you want to archive this claim?',
                    yesAction: 'Yes',
                  })
                    .then(async () => {
                      try {
                        await archiveEraClaim({
                          id: eraId,
                          eraId,
                          claimId: claim.id,
                          archived: true,
                        }).unwrap()
                        toast.success('Claim archived successfully')
                      } catch (e) {
                        toast.error(`There was an error archiving this claim`)
                      }
                    })
                    .catch(() => {})
                }}>
                Archive
              </Button>
              {claim.chargeId && (
                <Button
                  size="sm"
                  className="ml-2"
                  variant="outline-danger"
                  title="Park this remit on its charge instead of posting it, for someone to follow up"
                  onClick={(e) => {
                    e.stopPropagation()
                    setParking(true)
                  }}>
                  Needs Work
                </Button>
              )}
            </>
          )}
        </td>
      </StyledRow>
      {rowOpen && (
        <>
          <tr>
            <td colSpan={columnCount} className={'p-0'}>
              <div
                className="p-2"
                style={{ background: '#F4F6F9', borderBottom: '1px solid #c7c7c7' }}>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Serv. Date</th>
                        <th>POS</th>
                        <th>NOS</th>
                        <th>PROC</th>
                        <th>MODS</th>
                        <th>Billed $</th>
                        <th>Allowed $</th>
                        <th>Deduct $</th>
                        <th>Coins $</th>
                        <th>GRP/RC-AMT $</th>
                        <th>Remarks</th>
                        <th>Prov. PD $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(claim?.serviceLines ?? []).map((sl, si) => {
                        const normalizedAdjustments = normalizeCas(sl?.serviceAdjustments ?? [])

                        const billed = sl?.servicePaymentInformation?.lineItemChargeAmount

                        const proc =
                          sl?.servicePaymentInformation?.submittedAdjudicatedProcedureCode ||
                          sl?.servicePaymentInformation?.adjudicatedProcedureCode

                        const pos = claim?.pos

                        const nos = sl?.servicePaymentInformation?.unitsOfServicePaidCount

                        const mods = (
                          sl?.servicePaymentInformation
                            ?.submittedAdjudicatedProcedureModifierCodes ||
                          sl?.servicePaymentInformation?.adjudicatedProcedureModifierCodes ||
                          []
                        )?.join(', ')

                        const provPd = toNum(sl?.servicePaymentInformation?.lineItemProviderPaymentAmount)

                        const adjustmentSummaries = normalizedAdjustments.map((a) => ({
                          group: a?.group,
                          reason: a?.reason,
                          amount: toNum(a?.amount),
                        }))

                        const deduct = normalizedAdjustments
                          .filter((a) => a?.group === 'PR' && a?.reason === '1')
                          .reduce((acc, a) => acc + toNum(a?.amount), 0)

                        const coins = normalizedAdjustments
                          .filter((a) => a?.group === 'PR' && a?.reason === '2')
                          .reduce((acc, a) => acc + toNum(a?.amount), 0)

                        // Allowed is the posting service's reading of this line
                        // (AMT*B6, or billed less what the payer would not
                        // cover), so the EOB view and the posting agree.
                        const derivedLine = derivedLines?.[si]

                        return (
                          <tr key={si}>
                            <td>{formatCalendarDate(sl?.serviceDate)}</td>
                            <td>{pos || '--'}</td>
                            <td>{nos || '0'}</td>
                            <td>{proc || '--'}</td>
                            <td>{mods || '--'}</td>
                            <td>
                              <Money v={billed} />
                            </td>
                            {/* A dash means the plan came back without this
                                line (a reversal derives none); while it is
                                still loading, hold the space instead. */}
                            <td>
                              {derivedLine ? <Money v={derivedLine.allowed} /> : null}
                              {!derivedLine && (derivedLines ? '--' : <Pending />)}
                            </td>
                            <td>
                              <Money v={deduct} />
                            </td>
                            <td>
                              <Money v={coins} />
                            </td>
                            <td>
                              {adjustmentSummaries.length > 0 ? (
                                adjustmentSummaries.map((a, ai) => (
                                  <div key={ai}>
                                    <AdjustmentCode group={a.group} reason={a.reason} />{' '}
                                    <Money v={a.amount} />
                                  </div>
                                ))
                              ) : (
                                <span>N/A</span>
                              )}
                            </td>
                            <td>
                              {(sl?.healthCareCheckRemarkCodes?.length ?? 0) > 0 ? (
                                <RemarkCodeList codes={sl?.healthCareCheckRemarkCodes?.map((r: { remarkCode?: string }) => r.remarkCode)} />
                              ) : (
                                <span>N/A</span>
                              )}
                            </td>
                            <td>
                              <Money v={provPd} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {(claim?.remarkCodes?.length ?? 0) > 0 && (
                  <div className="pt-1" css={{ fontSize: '0.85em' }}>
                    <strong>Claim remarks:</strong> <RemarkCodeList codes={claim?.remarkCodes} />
                  </div>
                )}
              </div>
            </td>
          </tr>
          <tr style={{ background: '#F4F6F9', padding: 10 }}>
            <td colSpan={columnCount} className={'p-2'}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr css={{ '& > th': { fontWeight: 500 } }}>
                    <th>Sr. No</th>
                    <th>CPT</th>
                    <th>Balance</th>
                    <th>Allowed</th>
                    <th>Paid/Refund</th>
                    <th>Transfers</th>
                    <th>Write-Off</th>
                  </tr>
                </thead>
                <tbody>
                  <EraServiceLinesSummary
                    lines={derivedLines}
                    pendingRows={claim?.serviceLines?.length || 1}
                  />
                </tbody>
              </table>
            </td>
          </tr>
        </>
      )}
      <ApplyPaymentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        claim={claim}
        eraId={eraId}
        claimId={claimId as string}
      />
      {parking && claim.chargeId && (
        <NeedsWorkFlagModal
          target={{
            kind: 'remit',
            eraId,
            claimId: claim.id,
            chargeId: claim.chargeId,
            chargeLabel: claim.chargeOrderId ? `(order ${claim.chargeOrderId})` : null,
          }}
          onClose={() => setParking(false)}
        />
      )}
    </>
  )
}

interface Props {
  transactionId: string
  eraId: string
  query: BillingEraReportsListQuery
  selection?: EraClaimSelection
}

export const BillingTransactionClaims = ({ transactionId, eraId, query, selection }: Props) => {
  const { data: claims, isLoading } = useGetTransactionClaimsQuery({
    id: eraId,
    transactionId,
    query,
  })
  const reportClaims = selection?.reportClaims
  const eraClaimIds = selection?.getEraClaimIds(eraId) ?? []
  const eraFullySelected = eraClaimIds.length > 0 && eraClaimIds.every((id) => selection?.selectedIds.has(id))
  const eraSomeSelected = eraClaimIds.some((id) => selection?.selectedIds.has(id))

  React.useEffect(() => {
    if (!reportClaims || !claims) return

    reportClaims(transactionId, claims.filter(isClaimArchivable))
    return () => reportClaims(transactionId, null)
  }, [claims, transactionId, reportClaims])

  return isLoading ? (
    <BillingLoading />
  ) : (
    <div>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '10px 0',
          fontSize: 13,
          color: '#495057',
          flexWrap: 'wrap',
        }}>
        <span css={{ fontWeight: 600 }}>Legend:</span>
        <span css={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <SequenceBadge indicator="P" />
          Primary
        </span>
        <span css={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <SequenceBadge indicator="S" />
          Secondary
        </span>
      </div>
      <div className="table-responsive">
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              {selection && (
                <th style={{ width: 24 }}>
                  <input
                    type="checkbox"
                    aria-label="Select every claim in this ERA file"
                    title="Select every archivable claim in this ERA file matching the current filters"
                    disabled={!eraClaimIds.length}
                    checked={eraSomeSelected && eraFullySelected}
                    ref={(node) => {
                      if (node) {
                        node.indeterminate = eraSomeSelected && !eraFullySelected
                      }
                    }}
                    onChange={() => selection.toggleEra(eraId)}
                  />
                </th>
              )}
              <th style={{ width: 24 }} />
              <th style={{ width: 88, textAlign: 'center', whiteSpace: 'nowrap' }}>Auto-post</th>
              <th style={{ width: 56 }}>Payer</th>
              <th>Claim Identifier</th>
              <th>Claim Number</th>
              <th>Patient (Policyholder)</th>
              <th>DOS</th>
              <th>Paid</th>
              <th>Responsible</th>
              <th>Next Responsible</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{claims?.map((c, ci) => <ClaimRow key={ci} claim={c} eraId={eraId} selection={selection} />)}</tbody>
        </table>
      </div>
    </div>
  )
}
